import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  MenuItem
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Delete as DeleteIcon,
  Add as AddIcon,
  CloudUpload,
  Print,
  Visibility,
  TextSnippet,
  ReceiptLong
} from '@mui/icons-material';
import { format } from 'date-fns';
import { purchaseOrderService } from '../services/purchaseOrderService';
import { vendorService } from '../services/vendorService';
import { employeeService } from '../services/employeeService';
import { vendorInvoiceService } from '../services/vendorInvoiceService';
import { attachmentService } from '../services/attachmentService';
import { useAuth } from '../hooks/useAuth';
import {
  ProcurementSummaryBar,
  ProcurementSummaryField,
  PoReceiptStatusChip,
  getPoReceiptStatusChipProps,
  ShipmentsReceivedSummaryLinks
} from '../components/procurement/ProcurementSummaryBar';
import PrintPage from '../components/print/PrintPage';
import { printWithFilename } from '../utils/printUtils';
import '../styles/print.css';
import { formatCurrency } from '../components/CurrencyTextField';
import { computePOLineExtended, computePOLinesSubtotal } from '../utils/purchaseOrderLines';
import { formatDateOnly, formatLocalTodayYmd } from '../utils/dateUtils';

const SHIP_VIA_OPTIONS = ['UPS', 'USPS', 'DHL', 'FedEx', 'In-house carrier'];

const emptyPOForm = () => ({
  po_number: '',
  vendor_id: '',
  vendor_name: '',
  ordered_by_id: '',
  order_date: formatLocalTodayYmd(),
  expected_date: '',
  ship_via: '',
  notes: ''
});

const emptyLineItem = () => ({
  line_number: 1,
  part_id: null,
  part_number: '',
  description: '',
  quantity_ordered: 0,
  unit: 'EA',
  unit_price: '',
  notes: '',
  work_order_id: null,
  work_order_item_id: null
});

const ProcurementPODetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const printRequested = searchParams.get('print') === '1';
  const hasAutoPrintedRef = useRef(false);
  // `/procurement/po/new` is a static route (no `:id` param), so `id` can be undefined.
  // Detect "new" via pathname so we don't call `/api/purchase-orders/undefined` on mount.
  const isNew = id === 'new' || location.pathname.endsWith('/new');
  const prefillWorkOrderId = searchParams.get('work_order_id');
  const prefillLineItemIds = String(searchParams.get('line_item_ids') || '')
    .split(',')
    .map((v) => parseInt(v, 10))
    .filter((n) => Number.isFinite(n));

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [po, setPo] = useState(null);
  const [poForm, setPoForm] = useState(emptyPOForm());
  const [lineItems, setLineItems] = useState([emptyLineItem()]);
  const [vendors, setVendors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [linkedReceivers, setLinkedReceivers] = useState([]);
  const [linkedInvoices, setLinkedInvoices] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState('');
  const [isDraggingOverAttachment, setIsDraggingOverAttachment] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [identifierPreview, setIdentifierPreview] = useState('');
  const { user } = useAuth();
  const poPrintFilename = po?.identifier || po?.po_number || 'Purchase-Order';

  const poLinesForPrint = useMemo(
    () =>
      lineItems.filter(
        (it) => (it.part_number || it.description || '').trim() || Number(it.quantity_ordered) > 0
      ),
    [lineItems]
  );
  const poPrintSubtotal = useMemo(() => computePOLinesSubtotal(poLinesForPrint), [poLinesForPrint]);

  const previewLinkedWorkOrderId = useMemo(() => {
    const substantive = lineItems.filter(
      (it) => (it.part_number || it.description || '').trim() || Number(it.quantity_ordered) > 0
    );
    if (substantive.length === 0) return null;
    const allLinked = substantive.every(
      (it) => it.work_order_id != null && it.work_order_item_id != null
    );
    if (!allLinked) return null;
    const wids = [...new Set(substantive.map((it) => String(it.work_order_id)))];
    if (wids.length !== 1) return null;
    const wid = parseInt(wids[0], 10);
    return Number.isFinite(wid) ? wid : null;
  }, [lineItems]);

  // For the /procurement/po/new route, default "Ordered By" to the logged-in user.
  useEffect(() => {
    if (!isNew) return;
    if (!user?.id) return;
    setPoForm((f) => (f.ordered_by_id ? f : { ...f, ordered_by_id: Number(user.id) }));
  }, [isNew, user?.id]);

  useEffect(() => {
    const doPrefill = async () => {
      if (!isNew || !prefillWorkOrderId) return;
      try {
        const res = await purchaseOrderService.prefillFromOrder(prefillWorkOrderId, prefillLineItemIds);
        const header = res?.data?.header || {};
        const items = Array.isArray(res?.data?.items) ? res.data.items : [];
        if (items.length > 0) {
          setLineItems(items.map((it, idx) => ({
            line_number: it.line_number ?? idx + 1,
            part_id: it.part_id ?? null,
            part_number: it.part_number ?? '',
            description: it.description ?? '',
            quantity_ordered: parseInt(it.quantity_ordered, 10) || 0,
            unit: it.unit ?? 'EA',
            unit_price: it.unit_price ?? '',
            notes: it.notes ?? '',
            work_order_id: it.work_order_id ?? null,
            work_order_item_id: it.work_order_item_id ?? null
          })));
        }
        setPoForm((f) => ({
          ...f,
          order_date: header.order_date || f.order_date,
          notes: header.notes || f.notes
        }));
      } catch {
        // Non-blocking prefill
      }
    };
    doPrefill();
  }, [isNew, prefillWorkOrderId]);

  useEffect(() => {
    if (!isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await purchaseOrderService.getNextIdentifierPreview({
          order_date: poForm.order_date,
          work_order_id: previewLinkedWorkOrderId
        });
        const prev = res?.data?.identifier_preview ?? '';
        if (!cancelled) setIdentifierPreview(prev);
      } catch {
        if (!cancelled) setIdentifierPreview('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNew, poForm.order_date, previewLinkedWorkOrderId]);

  const loadAttachments = async (poId) => {
    if (!poId) {
      setAttachments([]);
      return;
    }
    try {
      setAttachmentsLoading(true);
      setAttachmentsError('');
      const res = await attachmentService.getForPurchaseOrder(poId);
      const list = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
      setAttachments(list);
    } catch (err) {
      setAttachmentsError(err.response?.data?.message || 'Failed to load attachments');
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const loadDetail = useCallback(async () => {
    if (isNew) return;
    try {
      setLoading(true);
      setError('');
      const [poRes, recRes, invRes] = await Promise.all([
        purchaseOrderService.getById(id),
        purchaseOrderService.getShipmentsInForPO(id),
        vendorInvoiceService.getAll({ purchase_order_id: id, limit: 100 })
      ]);
      const data = poRes?.data;
      setPo(data);
      setPoForm({
        po_number: data?.identifier ?? data?.po_number ?? '',
        vendor_id: data?.vendor_id ?? '',
        vendor_name: data?.vendor_name ?? '',
        ordered_by_id: data?.ordered_by_id ?? '',
        order_date: (data?.order_date || '').toString().slice(0, 10) || formatLocalTodayYmd(),
        expected_date: (data?.expected_date || '').toString().slice(0, 10) || '',
        ship_via: data?.ship_via ?? '',
        notes: data?.notes ?? ''
      });
      setLineItems(
        (data?.items || []).length > 0
          ? data.items.map((it, idx) => ({
              id: it.id,
              line_number: it.line_number ?? idx + 1,
              part_number: it.part_number ?? '',
              description: it.description ?? it.part_description ?? '',
              quantity_ordered: parseInt(it.quantity_ordered, 10) || 0,
              unit: it.unit ?? 'EA',
              unit_price: it.unit_price ?? '',
              notes: it.notes ?? '',
              work_order_id: it.work_order_id ?? null,
              work_order_item_id: it.work_order_item_id ?? null
            }))
          : [emptyLineItem()]
      );
      setLinkedReceivers(recRes?.data ?? []);
      setLinkedInvoices(invRes?.data ?? []);
      await loadAttachments(id);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load purchase order');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => {
    vendorService.getAll({ limit: 500, activeOnly: false }).then((res) => setVendors(res?.data ?? [])).catch(() => {});
    employeeService.getAll({ limit: 500, activeOnly: 'true' }).then((res) => setEmployees(res?.data ?? [])).catch(() => {});
  }, []);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    const preventDefault = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);
    return () => {
      document.removeEventListener('dragover', preventDefault);
      document.removeEventListener('drop', preventDefault);
    };
  }, []);

  useEffect(() => {
    if (isNew) return;
    if (!printRequested) return;
    if (loading) return;
    if (!po) return;
    if (hasAutoPrintedRef.current) return;
    hasAutoPrintedRef.current = true;
    printWithFilename(poPrintFilename, { delayMs: 500 });
  }, [isNew, printRequested, loading, po, poPrintFilename]);

  useEffect(() => {
    if (!printRequested) {
      hasAutoPrintedRef.current = false;
    }
  }, [printRequested]);

  const addLineItem = () => setLineItems((prev) => [...prev, { ...emptyLineItem(), line_number: prev.length + 1 }]);
  const updateLineItem = (idx, field, value) => setLineItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  const removeLineItem = (idx) => {
    setLineItems((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length === 0 ? [emptyLineItem()] : next.map((it, i) => ({ ...it, line_number: i + 1 }));
    });
  };

  const normalizedItems = () => lineItems
    .filter((it) => (it.part_number || it.description || '').trim() || Number(it.quantity_ordered) > 0)
    .map((it, idx) => ({
      line_number: idx + 1,
      part_id: it.part_id ?? null,
      part_number: it.part_number || null,
      description: it.description || null,
      quantity_ordered: Math.max(0, parseInt(it.quantity_ordered, 10) || 0),
      unit: it.unit || 'EA',
      unit_price: it.unit_price !== '' && it.unit_price != null ? Number(it.unit_price) : null,
      notes: it.notes || null,
      work_order_id: it.work_order_id ?? null,
      work_order_item_id: it.work_order_item_id ?? null
    }));

  const handleSave = async () => {
    const { vendor_id } = poForm;
    if (!vendor_id) return setError('Vendor is required.');
    const items = normalizedItems();
    if (items.length === 0) return setError('At least one line item is required.');
    try {
      setSaving(true);
      setError('');
      if (isNew) {
        const res = await purchaseOrderService.create({
          vendor_id: Number(poForm.vendor_id),
          ordered_by_id: poForm.ordered_by_id ? Number(poForm.ordered_by_id) : null,
          order_date: poForm.order_date,
          expected_date: poForm.expected_date || null,
          ship_via: poForm.ship_via || null,
          notes: poForm.notes || null,
          items
        });
        const newId = res?.data?.id ?? res?.id;
        if (newId) navigate(`/procurement/po/${newId}`);
        else navigate('/procurement/po');
      } else {
        await purchaseOrderService.update(id, {
          po_number: poForm.po_number,
          vendor_id: Number(poForm.vendor_id),
          ordered_by_id: poForm.ordered_by_id ? Number(poForm.ordered_by_id) : null,
          order_date: poForm.order_date,
          expected_date: poForm.expected_date || null,
          ship_via: poForm.ship_via || null,
          notes: poForm.notes || null,
          items
        });
        await loadDetail();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save purchase order');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelPO = async () => {
    if (isNew) return;
    try {
      setSaving(true);
      setError('');
      await purchaseOrderService.update(id, { status: 'CANCELLED' });
      setCancelConfirmOpen(false);
      await loadDetail();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel purchase order');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (v) => formatDateOnly(v, 'MM/dd/yyyy') || '—';

  const handleDelete = async () => {
    if (isNew) return navigate('/procurement/po');
    try {
      setSaving(true);
      setError('');
      await purchaseOrderService.delete(id);
      navigate('/procurement/po');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete purchase order');
    } finally {
      setSaving(false);
      setDeleteConfirmOpen(false);
    }
  };

  const uploadAttachments = async (files) => {
    if (isNew || !po?.id || !files?.length) return;
    if (files.length > 5) return setAttachmentsError('You can upload a maximum of 5 files at a time.');
    try {
      setAttachmentsError('');
      for (const file of files) {
        await attachmentService.upload({ file, purchaseOrderId: po.id });
      }
      await loadAttachments(po.id);
    } catch (err) {
      setAttachmentsError(err.response?.data?.message || 'Failed to upload attachment');
    }
  };

  const handleAttachmentDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOverAttachment(false);
    const fileList = event.dataTransfer?.files;
    if (!fileList || fileList.length === 0) return;
    await uploadAttachments(Array.from(fileList));
  };

  const handleDownloadAttachment = async (att) => {
    try {
      setAttachmentsError('');
      const res = await attachmentService.download(att.id);
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.file_name || `attachment-${att.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setAttachmentsError(err.response?.data?.message || 'Failed to download attachment');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>;
  }

  return (
    <>
      {!isNew && po && (
        <PrintPage
          title="Purchase Order"
          footer={
            <div className="print-footer">
              Generated on {format(new Date(), 'MM/dd/yyyy hh:mm a')}
            </div>
          }
        >
          <div className="print-quote-details-box print-section" style={{ marginBottom: 16 }}>
            <div className="print-quote-details-left">
              <div><strong>P.O. #:</strong> {po.identifier ?? po.po_number ?? '—'}</div>
              <div><strong>Order Date:</strong> {formatDate(po.order_date)}</div>
              <div><strong>Expected Date:</strong> {formatDate(po.expected_date)}</div>
              <div><strong>Ship Via:</strong> {po.ship_via || '—'}</div>
            </div>
            <div className="print-quote-details-right">
              <div>
                <strong>Receipt status:</strong> {getPoReceiptStatusChipProps(po.status).label}
              </div>
              <div><strong>Vendor:</strong> {po.vendor_name || '—'}</div>
              <div>
                <strong>Ordered By:</strong>{' '}
                {po.ordered_by_first_name || po.ordered_by_last_name
                  ? `${po.ordered_by_first_name || ''} ${po.ordered_by_last_name || ''}`.trim()
                  : '—'}
              </div>
            </div>
          </div>

          {!!po.notes && (
            <div className="print-section print-notes" style={{ marginBottom: 12 }}>
              <h3>Notes</h3>
              <p>{po.notes}</p>
            </div>
          )}

          <table className="print-table" style={{ width: '100%', marginBottom: 16 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Line</th>
                <th style={{ textAlign: 'left' }}>Part #</th>
                <th style={{ textAlign: 'left' }}>Description</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th style={{ textAlign: 'left' }}>Unit</th>
                <th style={{ textAlign: 'right' }}>Unit price</th>
                <th style={{ textAlign: 'right' }}>Line total</th>
              </tr>
            </thead>
            <tbody>
              {poLinesForPrint.length > 0 ? (
                poLinesForPrint.map((item, idx) => (
                  <tr key={item.id || idx}>
                    <td>{item.line_number ?? idx + 1}</td>
                    <td>{item.part_number || '—'}</td>
                    <td style={{ whiteSpace: 'pre-wrap' }}>
                      {item.description || '—'}
                      {item.notes ? `\n\nNotes: ${item.notes}` : ''}
                      {item.work_order_id != null ? `\n\nOrder ref: #${item.work_order_id}` : ''}
                    </td>
                    <td style={{ textAlign: 'right' }}>{item.quantity_ordered ?? 0}</td>
                    <td>{item.unit || 'EA'}</td>
                    <td style={{ textAlign: 'right' }}>
                      {item.unit_price != null && item.unit_price !== ''
                        ? formatCurrency(Number(item.unit_price))
                        : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(computePOLineExtended(item))}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center' }}>
                    No line items
                  </td>
                </tr>
              )}
            </tbody>
            {poLinesForPrint.length > 0 ? (
              <tfoot>
                <tr>
                  <td colSpan={6} style={{ textAlign: 'right' }}>
                    Total
                  </td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(poPrintSubtotal)}</td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </PrintPage>
      )}

      <Box className="no-print">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          {isNew ? 'New Purchase Order' : `Purchase Order ${po?.identifier ?? po?.po_number ?? ''}`.trim()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<ArrowBack />} onClick={() => navigate('/procurement/po')}>Back</Button>
          {!isNew && (
            <Button variant="outlined" size="small" startIcon={<Print />} onClick={() => printWithFilename(poPrintFilename)}>
              Print
            </Button>
          )}
          {!isNew && <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => setDeleteConfirmOpen(true)}>Delete</Button>}
          {!isNew && po?.status !== 'CANCELLED' && (
            <Button variant="outlined" color="warning" size="small" onClick={() => setCancelConfirmOpen(true)} disabled={saving}>
              Cancel P.O.
            </Button>
          )}
          <Button variant="contained" size="small" startIcon={<Save />} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

      {!isNew && po && (
        <ProcurementSummaryBar>
          <ProcurementSummaryField label="P.O. #">
            <Box sx={{ fontWeight: 600 }}>{po.identifier ?? po.po_number ?? '—'}</Box>
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Vendor">
            {po.vendor_id ? (
              <Button
                size="small"
                variant="text"
                sx={{ p: 0, minWidth: 'auto', textTransform: 'none', justifyContent: 'flex-start' }}
                onClick={() => navigate(`/procurement/vendors/${po.vendor_id}`)}
              >
                {po.vendor_name ?? '—'}
              </Button>
            ) : (
              (po.vendor_name ?? '—')
            )}
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Order Date">{formatDate(po.order_date)}</ProcurementSummaryField>
          <ProcurementSummaryField label="Expected Date">{formatDate(po.expected_date)}</ProcurementSummaryField>
          <ProcurementSummaryField label="Ship Via">{po.ship_via ?? '—'}</ProcurementSummaryField>
          <ProcurementSummaryField label="Receipt status">
            <PoReceiptStatusChip status={po.status} />
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Shipments received" wide>
            <ShipmentsReceivedSummaryLinks receivers={linkedReceivers} />
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Notes" wide>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{po.notes ?? '—'}</Box>
          </ProcurementSummaryField>
        </ProcurementSummaryBar>
      )}

      {!isNew && (
        <Paper sx={{ p: 1.5, mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Flow Links (PO → Shipment Received → Vendor Invoice)</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="small" variant="outlined" startIcon={<TextSnippet />} onClick={() => navigate(`/procurement/shipments-in/new?po=${encodeURIComponent(po?.po_number || '')}`)}>
              Create received shipment
            </Button>
            <Button size="small" variant="outlined" startIcon={<Visibility />} disabled={linkedReceivers.length === 0} onClick={() => navigate(`/procurement/shipments-in?po=${encodeURIComponent(po?.po_number || '')}`)}>
              View shipments ({linkedReceivers.length})
            </Button>
            <Button size="small" variant="outlined" startIcon={<ReceiptLong />} onClick={() => navigate(`/procurement/invoices?po=${id}&openNew=1`)}>
              Create vendor invoice
            </Button>
            <Button size="small" variant="outlined" startIcon={<Visibility />} disabled={linkedInvoices.length === 0} onClick={() => navigate(`/procurement/invoices?po=${id}`)}>
              View invoices ({linkedInvoices.length})
            </Button>
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={vendors}
              getOptionLabel={(opt) => opt?.name ?? ''}
              value={vendors.find((v) => v.id === poForm.vendor_id) ?? null}
              onChange={(_, val) => setPoForm((f) => ({ ...f, vendor_id: val?.id ?? '', vendor_name: val?.name ?? '' }))}
              renderInput={(params) => <TextField {...params} size="small" label="Vendor" required InputLabelProps={{ shrink: true }} />}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} />
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="P.O. #"
              value={isNew ? identifierPreview : (po?.identifier ?? poForm.po_number ?? '')}
              InputProps={{ readOnly: true }}
              helperText={isNew ? 'Assigned on save (preview updates when order date or order-linked lines change).' : ''}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          {/* Force Ordered By onto the next row so P.O. Number is alone */}
          <Grid item xs={12} sm={12} />
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={employees}
              getOptionLabel={(opt) => opt ? `${opt.first_name || ''} ${opt.last_name || ''}`.trim() || opt.email || '—' : ''}
              value={employees.find((e) => e.id === poForm.ordered_by_id) ?? null}
              onChange={(_, val) => setPoForm((f) => ({ ...f, ordered_by_id: val?.id ?? '' }))}
              renderInput={(params) => <TextField {...params} size="small" label="Ordered By" InputLabelProps={{ shrink: true }} />}
              size="small"
            />
          </Grid>
          {/* Keep Ordered By on its own row */}
          <Grid item xs={12} sm={6} />
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              select
              label="Ship via"
              value={poForm.ship_via || ''}
              onChange={(e) => setPoForm((f) => ({ ...f, ship_via: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {SHIP_VIA_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField fullWidth size="small" label="Order date" type="date" value={poForm.order_date} onChange={(e) => setPoForm((f) => ({ ...f, order_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField fullWidth size="small" label="Expected date" type="date" value={poForm.expected_date} onChange={(e) => setPoForm((f) => ({ ...f, expected_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
          </Grid>
          {/* Fill remaining grid space on Ship Via / dates row */}
          <Grid item xs={12} sm={2} />
          {/* Start Notes on its own row, left-aligned (same width as Ordered By) */}
          <Grid item xs={12} sm={12} />
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Notes"
              multiline
              rows={2}
              value={poForm.notes}
              onChange={(e) => setPoForm((f) => ({ ...f, notes: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} />
        </Grid>
      </Paper>

      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Line items</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><Typography variant="caption">Part #</Typography></TableCell>
                <TableCell><Typography variant="caption">Description</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption">Qty</Typography></TableCell>
                <TableCell><Typography variant="caption">Unit</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption">Unit price</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption">Line total</Typography></TableCell>
                <TableCell width={40} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lineItems.map((it, idx) => (
                <TableRow key={idx}>
                  <TableCell><TextField size="small" value={it.part_number} onChange={(e) => updateLineItem(idx, 'part_number', e.target.value)} sx={{ width: 120 }} /></TableCell>
                  <TableCell><TextField size="small" fullWidth value={it.description} onChange={(e) => updateLineItem(idx, 'description', e.target.value)} /></TableCell>
                  <TableCell align="right"><TextField type="number" size="small" value={it.quantity_ordered ?? ''} onChange={(e) => updateLineItem(idx, 'quantity_ordered', e.target.value)} sx={{ width: 90 }} /></TableCell>
                  <TableCell><TextField size="small" value={it.unit} onChange={(e) => updateLineItem(idx, 'unit', e.target.value)} sx={{ width: 70 }} /></TableCell>
                  <TableCell align="right"><TextField type="number" size="small" value={it.unit_price ?? ''} onChange={(e) => updateLineItem(idx, 'unit_price', e.target.value)} sx={{ width: 110 }} /></TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ pr: 0.5, whiteSpace: 'nowrap' }}>
                      {formatCurrency(computePOLineExtended(it))}
                    </Typography>
                  </TableCell>
                  <TableCell><IconButton size="small" onClick={() => removeLineItem(idx)} disabled={lineItems.length <= 1}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={5} align="right" sx={{ borderBottom: 'none', fontWeight: 600 }}>
                  <Typography variant="subtitle2">Total</Typography>
                </TableCell>
                <TableCell align="right" sx={{ borderBottom: 'none', fontWeight: 600 }}>
                  <Typography variant="subtitle2">{formatCurrency(computePOLinesSubtotal(lineItems))}</Typography>
                </TableCell>
                <TableCell sx={{ borderBottom: 'none' }} />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
        <Button size="small" startIcon={<AddIcon />} onClick={addLineItem} sx={{ mt: 0.75 }}>Add line</Button>
      </Paper>

      {!isNew && (
        <Paper sx={{ p: 1.5, mb: 1.5 }}>
          <Box
            onDrop={handleAttachmentDrop}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingOverAttachment(true); }}
            onDragLeave={(e) => { e.preventDefault(); if (!e.currentTarget.contains(e.relatedTarget)) setIsDraggingOverAttachment(false); }}
            sx={{
              p: 2, mb: 1, minHeight: 120, border: '2px dashed',
              borderColor: isDraggingOverAttachment ? 'primary.main' : 'grey.400',
              borderRadius: 2, bgcolor: isDraggingOverAttachment ? 'action.hover' : 'grey.50',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Drop documents for PO {po?.identifier ?? po?.po_number}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>or use Upload · max 5 files, 10 MB each</Typography>
            </Box>
            <Button variant="contained" component="label" size="small" startIcon={<CloudUpload />}>
              Upload
              <input type="file" hidden multiple onChange={async (e) => {
                const files = e.target.files;
                if (files?.length) await uploadAttachments(Array.from(files));
                e.target.value = '';
              }} />
            </Button>
          </Box>
          {attachmentsError && <Alert severity="error" sx={{ mb: 1 }}>{attachmentsError}</Alert>}
          {attachmentsLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={24} /></Box> : (
            attachments.length === 0 ? <Typography variant="body2" color="text.secondary">No attachments yet.</Typography> : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><Typography variant="caption">File Name</Typography></TableCell>
                      <TableCell><Typography variant="caption">Size</Typography></TableCell>
                      <TableCell><Typography variant="caption">Uploaded At</Typography></TableCell>
                      <TableCell width={80}><Typography variant="caption">Actions</Typography></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attachments.map((att) => (
                      <TableRow key={att.id} hover>
                        <TableCell><Button size="small" sx={{ textTransform: 'none' }} onClick={() => handleDownloadAttachment(att)}>{att.file_name}</Button></TableCell>
                        <TableCell>{att.file_size != null ? `${(att.file_size / 1024 / 1024).toFixed(2)} MB` : '—'}</TableCell>
                        <TableCell>{att.uploaded_at ? format(new Date(att.uploaded_at), 'MM/dd/yyyy HH:mm') : '—'}</TableCell>
                        <TableCell><IconButton size="small" color="error" onClick={async () => {
                          try {
                            await attachmentService.delete(att.id);
                            setAttachments((prev) => prev.filter((a) => a.id !== att.id));
                          } catch (err) {
                            setAttachmentsError(err.response?.data?.message || 'Failed to delete attachment');
                          }
                        }}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}
        </Paper>
      )}

      <Dialog open={cancelConfirmOpen} onClose={() => !saving && setCancelConfirmOpen(false)}>
        <DialogTitle>Cancel Purchase Order</DialogTitle>
        <DialogContent>
          <Typography>
            Mark P.O. <strong>{po?.identifier ?? po?.po_number ?? id}</strong> as <strong>CANCELLED</strong>? Receipt status will no longer update from shipments.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmOpen(false)} disabled={saving}>
            Back
          </Button>
          <Button variant="contained" color="warning" onClick={handleCancelPO} disabled={saving}>
            {saving ? 'Saving…' : 'Cancel P.O.'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => !saving && setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Purchase Order?</DialogTitle>
        <DialogContent>
          Delete <strong>{po?.identifier ?? po?.po_number ?? id}</strong>? This cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={saving}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={saving}>Delete</Button>
        </DialogActions>
      </Dialog>
      </Box>
    </>
  );
};

export default ProcurementPODetail;

